import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { Sparkle, TrendUp, TrendDown, Wallet, Plus } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, getCategoryLabel, getCategoryColor } from "@/lib/currency";

function StatCard({ bg, Icon, label, value, testId }) {
  return (
    <div className={`${bg} brutal-border rounded-xl brutal-shadow p-6`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 bg-white border-2 border-black rounded-lg flex items-center justify-center">
          <Icon size={18} weight="bold" />
        </div>
        <span className="text-xs font-bold uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-outfit font-black text-3xl sm:text-4xl">{value}</div>
    </div>
  );
}

function BrutalTooltip({ active, payload, label, currency }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="brutal-card p-3 text-sm font-manrope">
      {label && <div className="font-bold mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="flex items-center gap-2">
          <span className="w-3 h-3 border-2 border-black rounded-sm" style={{ background: p.color || p.payload?.color }} />
          <span>{p.name}: </span>
          <span className="font-bold">{formatMoney(p.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

function SummaryCards({ summary, currency }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <StatCard bg="bg-emerald-200" Icon={TrendUp} label="Income"
        value={formatMoney(summary.month_income, currency)} testId="stat-income" />
      <StatCard bg="bg-red-200" Icon={TrendDown} label="Expenses"
        value={formatMoney(summary.month_expense, currency)} testId="stat-expense" />
      <StatCard bg="bg-violet-200" Icon={Wallet} label="Balance"
        value={formatMoney(summary.month_balance, currency)} testId="stat-balance" />
    </div>
  );
}

function AITipsPanel({ tips, loading, onRefresh }) {
  return (
    <div className="relative brutal-card p-6 overflow-hidden">
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-yellow-200 border-2 border-black rounded-full opacity-70" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-violet-300 border-2 border-black rounded-full flex items-center justify-center brutal-shadow-sm">
            <Sparkle size={16} weight="fill" />
          </div>
          <h2 className="font-outfit font-black text-2xl">AI Budget Coach</h2>
          <button
            onClick={onRefresh}
            className="ml-auto brutal-btn bg-white hover:bg-yellow-100 px-3 py-1 text-xs"
            data-testid="refresh-tips-btn"
          >
            {loading ? "Thinking…" : "Refresh"}
          </button>
        </div>
        <div className="grid md:grid-cols-3 gap-3" data-testid="ai-tips-list">
          {tips === null && (
            <div className="col-span-full font-manrope text-black/50">Loading personalized tips…</div>
          )}
          {tips?.map((tip, i) => (
            <div key={`${i}-${tip.slice(0, 20)}`} className="bg-white border-2 border-black rounded-lg p-4 brutal-shadow-sm">
              <div className="text-xs font-bold uppercase tracking-widest text-black/40 mb-1">Tip {i + 1}</div>
              <p className="font-manrope font-semibold leading-snug">{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TrendChart({ data, currency }) {
  return (
    <div className="brutal-card p-6 lg:col-span-2" data-testid="trend-chart-card">
      <h3 className="font-outfit font-black text-2xl mb-4">6-month trend</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#09090B22" />
            <XAxis dataKey="month" stroke="#09090B" tick={{ fontFamily: "Manrope", fontWeight: 700 }} />
            <YAxis stroke="#09090B" tick={{ fontFamily: "Manrope", fontWeight: 700 }} />
            <Tooltip content={<BrutalTooltip currency={currency} />} />
            <Legend wrapperStyle={{ fontFamily: "Manrope", fontWeight: 700 }} />
            <Bar dataKey="income" fill="#A7F3D0" stroke="#09090B" strokeWidth={2} />
            <Bar dataKey="expense" fill="#FECACA" stroke="#09090B" strokeWidth={2} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CategoryChart({ pieData, currency }) {
  return (
    <div className="brutal-card p-6" data-testid="category-chart-card">
      <h3 className="font-outfit font-black text-2xl mb-4">By category</h3>
      {pieData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-black/50 font-manrope">
          No expenses yet this month.
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} stroke="#09090B" strokeWidth={2}>
                {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip content={<BrutalTooltip currency={currency} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-3 space-y-1.5">
        {pieData.map((d) => (
          <div key={d.name} className="flex items-center gap-2 text-sm font-manrope">
            <span className="w-3 h-3 rounded-sm border-2 border-black" style={{ backgroundColor: d.color }} />
            <span className="flex-1">{d.name}</span>
            <span className="font-bold">{formatMoney(d.value, currency)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currency } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tips, setTips] = useState(null);
  const [tipsLoading, setTipsLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setSummary(data);
    } catch (e) {
      console.error("Dashboard summary failed:", e);
    }
  }, []);

  const loadTips = useCallback(async () => {
    setTipsLoading(true);
    try {
      const { data } = await api.get("/insights");
      setTips(data.tips || []);
    } catch (e) {
      console.error("AI tips failed:", e);
      setTips(["Add some transactions to unlock AI-powered tips!"]);
    } finally {
      setTipsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadTips();
  }, [currency, loadSummary, loadTips]);

  if (!summary) {
    return <div className="brutal-card p-6 font-outfit font-black" data-testid="dashboard-loading">Loading dashboard…</div>;
  }

  const pieData = summary.by_category.map((c) => ({
    name: getCategoryLabel(c.category),
    value: c.amount,
    color: getCategoryColor(c.category),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
      data-testid="dashboard-page"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tight">Your money, this month.</h1>
          <p className="font-manrope text-black/60 mt-1">All values in {summary.base_currency}.</p>
        </div>
        <Link to="/transactions" className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2.5 flex items-center gap-2" data-testid="dashboard-add-btn">
          <Plus size={18} weight="bold" /> Add transaction
        </Link>
      </div>

      <SummaryCards summary={summary} currency={currency} />
      <AITipsPanel tips={tips} loading={tipsLoading} onRefresh={loadTips} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <TrendChart data={summary.trend} currency={currency} />
        <CategoryChart pieData={pieData} currency={currency} />
      </div>
    </motion.div>
  );
}
